import { Settings } from "../../../Settings";
import Fast from "../../../util/Fast";
import GridTile from "../../grid/GridTile";
import WorldGrid from "../../grid/WorldGrid";
import Vector from "../../math/Vector";
import Path from "../Path";

export default class DirectLinePathFinder {
    /**
     * @param {WorldGrid} worldGrid
     */
    constructor(worldGrid) {
        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {Vector}
         */
        this.movementVector = new Vector(0, 0);

        /**
         * @constant
         * @type {Vector}
         */
        this.position = new Vector(0, 0);

        /**
         * @constant
         * @type {number}
         */
        this.nearZero = 0.001;
    }

    /**
     * @param {Path} path 
     * @param {GridTile} startTile 
     * @param {GridTile} endTile 
     * @returns {boolean}
     */
    findPath(path, startTile, endTile) {

        // Don't check to see if the first tile is traversable, because we are standing on that tile.
        if (!startTile || !endTile) {
            return false;
        }

        //////////////////////////////////////////////
        // Case 1: start == end
        //////////////////////////////////////////////

        if (startTile === endTile) {
            //path.push(endTile);
            path.add(endTile);
            return true;
        }

        //////////////////////////////////////////////
        // Case 2: start adjacent to end
        //////////////////////////////////////////////

        if (startTile.isNeighborTile(endTile)) {
            path.add(startTile);
            path.add(endTile);
            return true;
        }

        this.position.copy(startTile.origin);

        let tile = startTile;
        path.add(tile);

        const step = Settings.tile.halfSize;

        this.movementVector.copy(endTile.origin);
        this.movementVector.subtract(this.position);
        const totalDistance = Fast.len(this.movementVector);

        // If the length is 0, then calculated values end up being NaN.
        if (Math.abs(totalDistance) < this.nearZero) {
            return true;
        }

        let traveledDistance = 0;

        // Convert movement vector into the correct length.
        this.movementVector.scale(step / totalDistance);

        let addedEnd = false;
        do {
            this.position.add(this.movementVector);
            traveledDistance += step;

            if (!tile.contains(this.position)) {

                const prev = tile;

                let neighbor = tile.getNeighbor(this.position); // <-- this handles diagonal neighbors too.
                if (!neighbor) {
                    console.log('DirectLinePathFinder.findPath() stepped to non-neighbor tile somehow!');

                    neighbor = this.worldGrid.findGridTile(this.position);
                    if (!neighbor) {
                        return false;
                    }
                }
                tile = neighbor;

                this.checkForDiagonal(path, prev, tile);

                path.add(tile);

                if (tile === endTile) {
                    addedEnd = true;
                    break;
                }
            }
        }
        while (traveledDistance < totalDistance && tile !== endTile);

        if (!addedEnd) {
            path.add(endTile);
        }

        return true;
    }

    /**
     * @private
     * @param {Path} path 
     * @param {GridTile} prev 
     * @param {GridTile} current 
     */
    checkForDiagonal(path, prev, current) {
        if (!current.isNeighborTile(prev)) {

            const left = prev.getNeighborLeft();
            const right = prev.getNeighborRight();
            const top = prev.getNeighborTop();
            const bottom = prev.getNeighborBottom();

            if (current === prev.getNeighborBottomLeft()) {
                // pick either bottom or left.
                if (bottom.open) {
                    path.add(bottom);
                }
                else {
                    path.add(left);
                }
            }
            else if (current === prev.getNeighborBottomRight()) {
                // pick either bottom or right.
                if (bottom.open) {
                    path.add(bottom);
                }
                else {
                    path.add(right);
                }
            }
            else if (current === prev.getNeighborTopLeft()) {
                // pick either top or left.
                if (top.open) {
                    path.add(top);
                }
                else {
                    path.add(left);
                }                    
            }
            else if (current === prev.getNeighborTopRight()) {
                // pick either top or right.
                if (top.open) {
                    path.add(top);
                }
                else {
                    path.add(right);
                }                                        
            }
        }
    }
}